import { BeginnAblaufKachelData, LaufzeitDisplayVariant } from '@ps20-frontend/gui-components';
import { ANZAHL_TAGE_TIER_ZU_JUNG, App, areAppTypesEquivalent } from '@ps20-frontend/frontend-models';
import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE, DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { Produktlinienname } from '@ps20/bff-models';
import { IAppState } from '../localStore';
import { Produkt, TkvCheckoutProdukt, TkvV2CheckoutProdukt, UnfCheckoutProdukt } from '../model';
import { Vorversicherung, VorversicherungsItem } from '../model/vorversicherung';
import { getProductnameForProduct, productsWithTierartCounter } from './tierartCounter.util';
import {
  getGeburtsdatumTierWennZuJung,
  getGeburtsdatumVp,
  selectedProdukte,
} from './produkt.selector';
import { getProdukteByRisikoort } from './selector.util';
import {
  selectIsHauptfaelligkeitLockedByMindestlaufzeit,
  selectIsMindestvertragslaufzeitActive,
} from './mindestvertragslaufzeit.selector';

const SOFORTSCHUTZ_MAX_MONTHS = 18;

export const selectBeginnAblaufdatumKachelData = (
  state: IAppState,
): Array<BeginnAblaufKachelData> => {
  const products = selectedProdukte(state)
    .filter(produkt => produkt !== null)
    .filter(produkt => selectExistsJHPOnly(state) || produkt.appId !== App.JHP);
  const productsWithTkvTierartCounterInVersichertesRisiko = productsWithTierartCounter(products);

  return getProductData(productsWithTkvTierartCounterInVersichertesRisiko, state);
};

export const selectBeginnAblaufdatumKachelDataJHPOnly = (
  state: IAppState,
): Array<BeginnAblaufKachelData> => {
  const products = selectedProdukte(state)
    .filter(produkt => produkt !== null)
    .filter(produkt => !selectExistsJHPOnly(state) && produkt.appId === App.JHP);
  const productsWithTkvTierartCounterInVersichertesRisiko = productsWithTierartCounter(products);

  return getProductData(productsWithTkvTierartCounterInVersichertesRisiko, state);
};

function isUnfall(produkt: Produkt): boolean {
  return produkt.appId === App.UNF;
}

function getIsUnfallhilfeSofortBesprochen(state: IAppState, produkt: Produkt): boolean {
  return isUnfall(produkt)
    ? state.unfAngaben?.vpAngabenListe.find(vpAngabe => vpAngabe.angebotsId === produkt.angebotsId)
        .isSofortschutzBesprochen
    : undefined;
}

const getProductData = (products: Produkt[], state: IAppState): Array<BeginnAblaufKachelData> => {
  return products.map(produkt => {
    const isErsatzgeschaeft = produkt.vorvertragsnummer?.length > 0;
    const isBeginnMoreThanOneDayInFuture =
      dayjs(produkt.beginn, DATEFORMAT_LOCAL_STATE)
        .startOf('day')
        .diff(dayjs().startOf('day'), 'days') > 1;
    const geburtsdatumTierWennZuJung = getAnimalBirthdateIfTooYoung(produkt);
    const geburtsdatumVp = isUnfall(produkt)
      ? getGeburtsdatumVp(produkt as UnfCheckoutProdukt)
      : undefined;

    const vorversicherungsItems = getVorversicherungsItemsForProdukt(
      state.vorversicherungen,
      produkt,
      state,
    );
    const laufzeitDisplayVariant: LaufzeitDisplayVariant = selectIsMindestvertragslaufzeitActive(
      state,
    )
      ? LaufzeitDisplayVariant.MINDESTLAUFZEIT_PLAIN
      : LaufzeitDisplayVariant.REGULAR;
    const maxAblaufdatum = getMaxAblaufdatum(vorversicherungsItems);
    const standardMaxDate = getMaxDate();

    let calculatedMaxDate = standardMaxDate.format(DATEFORMAT_SHORT_ISO);

    if (maxAblaufdatum && produkt.sofortschutzAvailable) {
      const referenceDate = getSofortschutzReferenceDate(produkt);
      const latestExpirationDate = dayjs(maxAblaufdatum, DATEFORMAT_LOCAL_STATE);
      const eighteenMonthThreshold = referenceDate.add(SOFORTSCHUTZ_MAX_MONTHS, 'months');

      if (latestExpirationDate.isAfter(eighteenMonthThreshold)) {
        const sofortschutzMaxDate = latestExpirationDate.subtract(SOFORTSCHUTZ_MAX_MONTHS, 'months');

        if (sofortschutzMaxDate.isBefore(standardMaxDate)) {
          calculatedMaxDate = sofortschutzMaxDate.format(DATEFORMAT_SHORT_ISO);
        }
      }
    }

    return {
      produktId: produkt.appId,
      angebotsId: produkt.angebotsId,
      componentSuffix: getComponentSuffix(produkt, state.produkte),
      produktName: getProductnameForProduct(produkt),
      beginn: produkt.beginn,
      ablauf: produkt.ablauf,
      laufzeit: produkt.laufzeit,
      laufzeitDisplayVariant,
      risikoort: getRisikoort(produkt),
      disabled: shouldDisableDatePicker(state.vorversicherungen, produkt, state),
      isSofortschutzActive: produkt.sofortschutzActive,
      isUnfallhilfeSofortBesprochen: getIsUnfallhilfeSofortBesprochen(state, produkt),
      showSofortschutzHinweistext: shouldShowSofortschutzHinweistext(
        state.vorversicherungen,
        produkt,
        state,
      ),
      sofortschutzEndeDatum: dayjs(produkt.sofortschutzAblauf).format(DATEFORMAT_LOCAL_STATE),
      minDate: geburtsdatumTierWennZuJung
        ? getMinDateTkv(geburtsdatumTierWennZuJung)
        : getMinDate(),
      maxDate: calculatedMaxDate,
      wirksamkeitsbeginn: geburtsdatumTierWennZuJung
        ? getWirksamkeitsbeginnTkv(geburtsdatumTierWennZuJung)
        : produkt.wirksamkeitsbeginn,
      shouldDisableBeginndatum: produkt.shouldDisableBeginndatum,
      beitragsfreiBeibehalten: produkt.beitragsfreiBeibehalten,
      infoTextKey: getInfoTextKey(produkt),
      showBeitragsanpassungInfo: isErsatzgeschaeft && isBeginnMoreThanOneDayInFuture,
      geburtsdatumTierWennZuJung,
      geburtsdatumVp,
    };
  });
};

function getInfoTextKey(produkt: Produkt): string {
  switch (produkt.appId) {
    case App.JHP:
      return 'beginnundablauf.info-jhp';
    case App.GEGE:
      return 'beginnundablauf.info-gege';
    case App.UNF: {
      if (produkt.produktlinie === Produktlinienname.KOMFORT) {
        return 'beginnundablauf.info-unf-komfort';
      } else {
        return 'beginnundablauf.info-unf';
      }
    }

    default:
      return undefined;
  }
}

export const selectHauptfaelligkeit = (state: IAppState): string => {
  return state?.hauptfaelligkeit;
};

export const selectDisableHauptfaelligkeitInput = (state: IAppState): boolean => {
  return selectIsHauptfaelligkeitLockedByMindestlaufzeit(state) || selectExistsJHPOnly(state);
};

export const selectExistsJHPOnly = (state: IAppState): boolean => {
  return !state.produkte.some(proukt => proukt.appId != App.JHP);
};

export const selectIsLaufzeit3JahreForAllProducts = (state: IAppState): boolean => {
  return state.produkte && state.produkte.every(produkt => produkt.laufzeit === 3);
};

export function getComponentSuffix(produkt: Produkt, produkte: Array<Produkt>): string {
  const appKeyAsString = Object.keys(App)
    .find(key => (App as Record<string, unknown>)[key] === produkt.appId)
    .toLocaleLowerCase();

  if (produkt.angebotsId) {
    const produktWithAppId = produkte.filter(elem => elem.appId === produkt.appId);

    if (produktWithAppId.length > 1) {
      return (
        appKeyAsString +
        produktWithAppId.findIndex(elem => elem.angebotsId === produkt.angebotsId).toString()
      );
    }
  }

  return appKeyAsString;
}

function isSomeAblaufdatumAfterReferenceDate(
  vorversicherungsItems: Array<VorversicherungsItem>,
  referenceDate: dayjs.Dayjs,
): boolean {
  return vorversicherungsItems.some(
    vorversicherungItem =>
      vorversicherungItem.ablaufdatum !== null &&
      dayjs(vorversicherungItem.ablaufdatum, DATEFORMAT_LOCAL_STATE).isAfter(
        referenceDate.add(1, 'day'),
      ),
  );
}

export function getMaxAblaufdatum(
  vorversicherungsItems: Array<VorversicherungsItem>,
): string | null {
  const ablaufdatums = vorversicherungsItems
    .filter(item => item.ablaufdatum !== null)
    .map(item => dayjs(item.ablaufdatum, DATEFORMAT_LOCAL_STATE));

  if (ablaufdatums.length === 0) {
    return null;
  }

  const maxAblaufdatum = ablaufdatums.reduce(
    (max, current) => (current.isAfter(max) ? current : max),
    ablaufdatums[0],
  );

  return maxAblaufdatum.format(DATEFORMAT_LOCAL_STATE);
}

export function isExpirationMoreThan18MonthsAway(
  maxAblaufdatum: string | null,
  produkt: Produkt,
): boolean {
  if (!maxAblaufdatum || !produkt.sofortschutzAvailable) {
    return false;
  }

  const referenceDate = getSofortschutzReferenceDate(produkt);
  const latestExpirationDate = dayjs(maxAblaufdatum, DATEFORMAT_LOCAL_STATE);
  const eighteenMonthThreshold = referenceDate.add(SOFORTSCHUTZ_MAX_MONTHS, 'months');

  return latestExpirationDate.isAfter(eighteenMonthThreshold);
}

export function shouldDisableDatePicker(
  vorversicherungen: Array<Vorversicherung>,
  produkt: Produkt,
  state: IAppState,
): boolean {
  const vorversicherungsItems = getVorversicherungsItemsForProdukt(
    vorversicherungen,
    produkt,
    state,
  );
  const isUnfallhilfeSofortBesprochen = getIsUnfallhilfeSofortBesprochen(state, produkt);
  const isSofortschutzAvailable = isUnfall(produkt)
    ? produkt.sofortschutzAvailable && isUnfallhilfeSofortBesprochen !== false
    : produkt.sofortschutzAvailable;

  const maxAblaufdatum = getMaxAblaufdatum(vorversicherungsItems);
  const isExpirationMoreThan18Months = isExpirationMoreThan18MonthsAway(maxAblaufdatum, produkt);

  if (isSofortschutzAvailable && isExpirationMoreThan18Months) {
    return !!produkt.shouldDisableBeginndatum;
  }

  return (
    (isSofortschutzAvailable && !produkt.beginndatumChangeable) ||
    !!produkt.shouldDisableBeginndatum
  );
}

function shouldShowSofortschutzHinweistext(
  vorversicherungen: Array<Vorversicherung>,
  produkt: Produkt,
  state: IAppState,
): boolean {
  const vorversicherungsItems = getVorversicherungsItemsForProdukt(
    vorversicherungen,
    produkt,
    state,
  );
  const ablaufDate = dayjs(produkt.sofortschutzAblauf, 'YYYY-MM-DD');

  const referenceDate = getSofortschutzReferenceDate(produkt);
  const maxAllowedAblauf = referenceDate.add(SOFORTSCHUTZ_MAX_MONTHS, 'months');

  const tkvProduktWithoutSofortschutz =
    areAppTypesEquivalent(produkt.appId, App.TKV) && !produkt.sofortschutzActive;

  if (
    produkt.sofortschutzAvailable &&
    isSomeAblaufdatumAfterReferenceDate(vorversicherungsItems, referenceDate)
  ) {
    if (ablaufDate.isBefore(maxAllowedAblauf)) {
      return !tkvProduktWithoutSofortschutz;
    }
    if (ablaufDate.isAfter(maxAllowedAblauf) || ablaufDate.isSame(maxAllowedAblauf)) {
      return true;
    }
  }

  return false;
}

function getVorversicherungsItemsForProdukt(
  vorversicherungen: Array<Vorversicherung>,
  produkt: Produkt,
  state: IAppState,
): Array<VorversicherungsItem> {
  const unfProdukte = getProdukteByRisikoort(App.UNF, produkt.versichertesRisiko, state.produkte);
  const vorversicherung = vorversicherungen.find(
    vorvers =>
      (vorvers.appId !== App.UNF &&
        vorvers.appId === produkt.appId &&
        vorvers.angebotsId === produkt.angebotsId) ||
      (vorvers.appId === App.UNF &&
        vorvers.appId === produkt.appId &&
        unfProdukte.some(prod => vorvers.angebotsId == prod.angebotsId)),
  );
  return vorversicherung != null ? vorversicherung.vorversicherungsListe : [];
}

export function getRisikoort(produkt: Produkt): string {
  return produkt.versichertesRisiko ? produkt.versichertesRisiko : '';
}

function getWirksamkeitsbeginnTkv(geburtsdatumTier: string): string {
  return dayjs(geburtsdatumTier, DATEFORMAT_LOCAL_STATE)
    .add(ANZAHL_TAGE_TIER_ZU_JUNG, 'd')
    .format(DATEFORMAT_LOCAL_STATE);
}

function getMinDateTkv(geburtsdatumTier: string): string {
  return dayjs(geburtsdatumTier, DATEFORMAT_LOCAL_STATE)
    .add(ANZAHL_TAGE_TIER_ZU_JUNG, 'd')
    .format(DATEFORMAT_SHORT_ISO);
}

function getMinDate(): string {
  return getCurrentDay().startOf('day').add(1, 'd').format(DATEFORMAT_SHORT_ISO);
}

function getMaxDate(): dayjs.Dayjs {
  return getCurrentDay().startOf('day').add(1, 'year').subtract(1, 'd');
}

function getAnimalBirthdateIfTooYoung(produkt: Produkt): string | undefined {
  if (produkt.appId === App.TKV) {
    return getGeburtsdatumTierWennZuJung((produkt as TkvCheckoutProdukt).geburtsdatumTier);
  }
  if (produkt.appId === App.TKV_V2) {
    return getGeburtsdatumTierWennZuJung((produkt as TkvV2CheckoutProdukt).animalBirthdate);
  }
  return undefined;
}

function getSofortschutzReferenceDate(produkt: Produkt): dayjs.Dayjs {
  const geburtsdatumTierWennZuJung = getAnimalBirthdateIfTooYoung(produkt);
  if (geburtsdatumTierWennZuJung) {
    return dayjs(geburtsdatumTierWennZuJung, DATEFORMAT_LOCAL_STATE).add(
      ANZAHL_TAGE_TIER_ZU_JUNG,
      'd',
    );
  }
  return getCurrentDay();
}

export const getCurrentDay = (): dayjs.Dayjs => {
  return dayjs();
};
